import React, { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { faUsers } from '@fortawesome/pro-thin-svg-icons';
import { keyBy } from 'lodash-es';

import { RosterClient, Team } from 'jf/api';
import { DevExPageHeader } from 'jf/common/DevExPageHeader';
import { DevExPageLayout } from 'jf/common/DevExPageLayout';
import { DevExPageTitle } from 'jf/common/DevExPageTitle';
import { DevExCheckbox } from 'jf/components/DevExCheckbox';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExExpandableAlert } from 'jf/components/DevExExpandableAlert';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExRadioButtons } from 'jf/components/DevExRadioButtons';
import { DevExSearch } from 'jf/components/DevExSearch';
import { DevExText } from 'jf/components/DevExText';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { CATCH_ALL_TEAM_REF } from '../study/question/QuestionCardDemographic';

import { EditableTeamHierarchy } from './EditableTeamHierarchy';

type TeamsFilter = 'ALL' | 'HIDDEN' | 'DEVEX';

const TEAMS_FILTER_LABELS: { [key in TeamsFilter]: string } = {
  ALL: 'All',
  HIDDEN: 'Hidden',
  DEVEX: 'DevEx Only',
};

/**
 * Filter teams by a filtering function, while also including all parent teams above the filtered teams.
 */
export const filterTeamsInHierarchy = (teams: Team[], filterFn: (team: Team) => boolean) => {
  const filteredTeams = teams.filter(filterFn);

  const teamVisibilty: { [ref: string]: boolean } = {};
  const teamsByRef = keyBy(teams, 'ref');

  // include parents of filtered teams
  for (const filteredTeam of filteredTeams) {
    let team = filteredTeam;
    teamVisibilty[team.ref] = true;

    while (team.parentRef && !teamVisibilty[team.parentRef] && !!teamsByRef[team.parentRef]) {
      team = teamsByRef[team.parentRef];
      teamVisibilty[team.ref] = true;
    }
  }

  return teams.filter((team) => teamVisibilty[team.ref]);
};

export const TeamsPage: React.FC = () => {
  const flags = useClientFlags();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<TeamsFilter>('ALL');
  const [showInactive, setShowInactive] = useState(false);

  const { data: teams, isLoading } = useClientQuery(
    RosterClient.getTeams,
    flags.showInactiveDevExTeams && showInactive ? { includeInactive: true } : {},
    {
      queryKey: ['GET_TEAMS', flags.showInactiveDevExTeams && showInactive],
    }
  );

  // Keep previous teams data during loading to maintain filter visibility
  const [previousTeams, setPreviousTeams] = useState<Team[] | undefined>(undefined);
  useEffect(() => {
    if (teams) {
      setPreviousTeams(teams);
    }
  }, [teams]);

  const editableTeams = useMemo(() => {
    const teamsToUse = teams || previousTeams;
    return teamsToUse?.filter((team) => team.ref !== CATCH_ALL_TEAM_REF);
  }, [teams, previousTeams]);

  const visibleTeams = useMemo(() => {
    if (!editableTeams) {
      return [];
    }

    // First, filter teams based on user's criteria
    const filteredTeams = editableTeams.filter((team) => {
      // ingore "Unspecified" team
      if (team.ref === CATCH_ALL_TEAM_REF) {
        return false;
      }

      // filter out inactive teams unless showInactive is true and feature flag is enabled
      if (flags.showInactiveDevExTeams && !showInactive && team.isActive === false) {
        return false;
      } else if (!flags.showInactiveDevExTeams && team.isActive === false) {
        // Always filter out inactive teams if feature flag is disabled
        return false;
      }

      // apply search query
      if (searchQuery && !team.value.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // apply filter
      if (filter === 'ALL') {
        return true;
      } else if (filter === 'HIDDEN') {
        return team.isHidden;
      } else if (filter === 'DEVEX') {
        return !team.isEmp;
      }

      return false;
    });

    // Then, include parents of filtered teams to maintain hierarchy
    // Note: If a parent is inactive and showInactive=false, the parent won't be in editableTeams
    // (filtered by backend), so the child will appear orphaned at root level
    const teamVisibilty: { [ref: string]: boolean } = {};
    const teamsByRef = keyBy(editableTeams, 'ref');

    for (const filteredTeam of filteredTeams) {
      let team = filteredTeam;
      teamVisibilty[team.ref] = true;

      // Walk up the parent chain and include all parents that exist in the array
      // If parent doesn't exist (e.g., inactive and filtered out), stop walking up
      // The team will appear at root level (orphaned)
      while (team.parentRef && !teamVisibilty[team.parentRef] && teamsByRef[team.parentRef]) {
        team = teamsByRef[team.parentRef];
        teamVisibilty[team.ref] = true;
      }
    }

    return editableTeams.filter((team) => teamVisibilty[team.ref]);
  }, [editableTeams, searchQuery, filter, showInactive]);

  const teamsFilterOptions = useMemo(() => {
    if (!editableTeams) {
      return [];
    }

    const optionKeys: TeamsFilter[] = ['ALL'];
    if (editableTeams.some((team) => team.isHidden)) {
      optionKeys.push('HIDDEN');
    }
    if (editableTeams.some((team) => !team.isEmp) && window.dx.user?.company.isEmp) {
      optionKeys.push('DEVEX');
    }

    return optionKeys.map((key) => ({ key, label: TEAMS_FILTER_LABELS[key] }));
  }, [editableTeams]);

  const areAnyTeamsEditable = !!editableTeams?.length;
  const areAnyTeamsVisible = !!visibleTeams?.length;
  const isWaitingOnSync = !areAnyTeamsEditable && window.dx.user?.company.isEmp && !isLoading;
  const isFlatTeamHierarchy = !editableTeams?.some((team) => team.parentRef);

  return (
    <Flex vertical align="center" style={{ flex: 1 }}>
      <DevExPageTitle>DevEx Teams</DevExPageTitle>
      <DevExPageHeader
        title="Teams"
        subtitle="Manage the teams available for targeting in surveys. Teams that are hidden will be excluded from targeting selection."
      />

      <DevExPageLayout style={{ flex: 1 }}>
        {(areAnyTeamsEditable || isLoading) && (
          <Flex justify="space-between" align="center">
            <Flex gap={16} align="center">
              {teamsFilterOptions.length > 1 ? (
                <DevExRadioButtons
                  value={filter}
                  onChange={(value: TeamsFilter) => setFilter(value)}
                  items={teamsFilterOptions}
                />
              ) : (
                <div />
              )}

              {flags.showInactiveDevExTeams && (
                <DevExCheckbox checked={showInactive} onChange={setShowInactive}>
                  <DevExText>Show inactive teams</DevExText>
                </DevExCheckbox>
              )}
            </Flex>

            <DevExSearch
              placeholder="Search for a team"
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </Flex>
        )}

        {isLoading && !teams ? (
          <DevExLoader />
        ) : (
          <>
            {isLoading && teams && <DevExLoader />}

            {isWaitingOnSync && (
              <DevExExpandableAlert title="Your teams will be synchronized from Jellyfish after creating your first survey." />
            )}

            {!isWaitingOnSync && (
              <EditableTeamHierarchy teams={visibleTeams} isFlat={isFlatTeamHierarchy} />
            )}

            {areAnyTeamsEditable && !areAnyTeamsVisible && !isLoading && (
              <DevExEmpty icon={faUsers} label="No teams match your filter and search." />
            )}
          </>
        )}
      </DevExPageLayout>
    </Flex>
  );
};
